/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import { Field } from 'redux-form'
import { RouteComponentProps } from 'react-router-dom'
import classNames from 'classnames/bind'
import { MAX_LENGTHS } from 'constants/forms'
import { Text as TextType, GetTextPath } from 'hocs/localization'
import { Input, Captcha } from 'apps/FormFields'
import Button from 'elements/Button'
import {
  createValidation,
  createValidator,
  hasValidPassword,
  hasPasswordUppercaseLetters,
  hasPasswordLowercaseLetters,
  hasPasswordSpecialCharacters,
  hasPasswordNumbers,
  hasPasswordValidLength,
  removeSpaces,
} from 'utils/form'
import { STEPS } from './constants'
import * as styles from './ChangePasswordForm.scss'

const cx = classNames.bind(styles)

interface IProps extends RouteComponentProps {
  t: TextType,
  getTextPath: GetTextPath,
  handleSubmit: any,
  step: any,
  isSent: boolean,
  valid: boolean,
  submitting: boolean,
  isLinkExpired: boolean,
  isCaptchaEnabled: boolean,
  isCaptchaWhiteListed: boolean,
  onResendForgotPassword: () => Promise<any>,
}

interface IState {
  isCaptchaVerified: boolean,
}

class ChangePasswordForm extends React.Component<IProps, IState> {
  public state = {
    isCaptchaVerified: false,
  }

  public passwordValidation = createValidation([
    createValidator(hasValidPassword, this.props.getTextPath('errors.password')),
    createValidator(hasPasswordUppercaseLetters, this.props.getTextPath('errors.password.uppercaseLetter')),
    createValidator(hasPasswordLowercaseLetters, this.props.getTextPath('errors.password.lowercaseLetter')),
    createValidator(hasPasswordNumbers, this.props.getTextPath('errors.password.number')),
    createValidator(hasPasswordSpecialCharacters, this.props.getTextPath('errors.password.specialCharacter')),
    createValidator(hasPasswordValidLength, this.props.getTextPath('errors.password.length')),
  ])

  private onVerifyCaptcha = response => {
    this.setState({
      isCaptchaVerified: !!response,
    })
  }

  public render () {
    const {
      t,
      handleSubmit,
      valid,
      submitting,
      isCaptchaEnabled,
      isCaptchaWhiteListed,
      step,
      isSent,
      onResendForgotPassword,
    } = this.props
    const {
      isCaptchaVerified,
    } = this.state
    if (isSent) {
      return (
        <div className={styles.message}>
          {t('linkExpired.resendEmail')}
        </div>
      )
    }
    if (step === null) {
      return(
        <div>
          <div className={styles.ChangePassTitle}>
            <h2>{t('form.header')}</h2>
          </div>
          <form className={styles.ChangePassForm} onSubmit={handleSubmit}>
            <Field
              name='password'
              type='password'
              component={Input}
              validate={this.passwordValidation}
              maxLength={MAX_LENGTHS.password}
              normalize={removeSpaces}
              label={t('form.passwordPlaceholder')}
              withPrompt
              newLineError
            />
            <Field
              name='confirmPassword'
              type='password'
              component={Input}
              maxLength={MAX_LENGTHS.password}
              normalize={removeSpaces}
              label={t('form.confirmPasswordPlaceholder')}
            />
            {isCaptchaEnabled && !isCaptchaWhiteListed && (
              <div className={cx(styles.centered, styles.FormField)}>
                <Field
                  name='captcha'
                  component={Captcha}
                  verifyCallback={this.onVerifyCaptcha}
                />
              </div>
            )}
            <div className={cx(styles.FormField, styles.NextButton, styles.SaveChange, 'text-center')}>
              <Button fluid disabled={!valid
                || (isCaptchaEnabled && !isCaptchaWhiteListed && !isCaptchaVerified)
                || submitting}>
                {submitting ? t('form.submitButtonSubmitting') : t('form.submitButton')}
              </Button>
            </div>
          </form>
        </div>
      )
    }
    if (step === STEPS.used) {
      return (
        <div className={styles.message}>
          {t('linkUsed')}
        </div>
      )
    }
    if (step === STEPS.expired) {
      const link = (<a href='#' onClick={onResendForgotPassword}>{t('linkExpired.linkToNew')}</a>)
      return (
        <div className={styles.message}>
          {t('linkExpired.messageStart')} {link} {t('linkExpired.messageEnd')}
        </div>
      )
    }
  }
}

export default ChangePasswordForm
